import { ChiefAPIError } from "@api/types/backendTypes";
import axiosInstance from "@/lib/axios";
import {
  postbackSourceStatusQueryKey,
  type PostbackSourceStatus,
} from "./useGetPostbackSourceStatus";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { z } from "zod";

const SetPostbackSourceStatusSchema = z.object({
  source: z.enum([
    "facebook",
    "google",
    "klaviyo",
    "pinterest",
    "tiktok",
    "outbrain",
    "msads",
    "taboola",
  ]),
  organisationId: z.string(),
  isActiveForPostback: z.boolean(),
});

export type SetPostbackSourceStatusInput = z.infer<
  typeof SetPostbackSourceStatusSchema
>;

const updateActiveForPostback = (
  activeForPostback: string[] | undefined,
  source: string,
  isActiveForPostback: boolean
) => {
  const nextActiveForPostback = new Set(activeForPostback ?? []);

  if (isActiveForPostback) {
    nextActiveForPostback.add(source);
  } else {
    nextActiveForPostback.delete(source);
  }

  return Array.from(nextActiveForPostback);
};

const onSetPostbackSourceStatus = async (
  input: SetPostbackSourceStatusInput
) => {
  try {
    SetPostbackSourceStatusSchema.parse(input);

    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/organisations/${input.organisationId}/update`,
      {
        set_sources_active_for_postback: input.isActiveForPostback
          ? [input.source]
          : [],
        set_sources_inactive_for_postback: input.isActiveForPostback
          ? []
          : [input.source],
      }
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const apiError = axiosError.response?.data as ChiefAPIError;
      const firstErr = apiError.detail?.[0];
      throw new Error(firstErr?.msg ?? axiosError.message);
    }
    throw new Error("Unknown error occurred!");
  }
};

export const useSetPostbackSourceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetPostbackSourceStatusInput) =>
      onSetPostbackSourceStatus(input),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<PostbackSourceStatus | undefined>(
        postbackSourceStatusQueryKey(variables.organisationId),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            activeForPostback: updateActiveForPostback(
              current.activeForPostback,
              variables.source,
              variables.isActiveForPostback
            ),
          };
        }
      );
      queryClient.invalidateQueries({
        queryKey: postbackSourceStatusQueryKey(variables.organisationId),
      });
    },
  });
};
